import React, { useEffect, useState } from 'react';
import produce from 'immer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './Stats.scss';
import { ErrorBoundary, ErrorMessage, Loader, Page, PageHeader } from 'components';
import { Button} from '@cfa/react-components';
import { StatsService } from 'services';
import { IStats } from 'interfaces';
import { AsyncStatus } from 'enums';
import { EmergencyStopModal } from './EmergencyStopModal';

interface IStatsPageState {
    loadStatus: AsyncStatus;
    loadError?: string;
    stats?: IStats;
}

export function Stats() {
    const [pageState, setPageState] = useState<IStatsPageState>({
        loadStatus: AsyncStatus.Loading,
        stats: StatsService.stats,
    });
    const { stats, loadStatus, loadError } = pageState;
    const [showEmergencyStop, setShowEmergencyStop] = useState(false);

    useEffect(() => {
        if (loadStatus === AsyncStatus.Success) return;

        StatsService.getStats()
            .then((res) => {
                setPageState(
                    produce((draft) => {
                        draft.stats = res;
                        draft.loadStatus = AsyncStatus.Success;
                    })
                );
            });
    }, []);

    return (<>
        <Page>
            <PageHeader title="Admin Statistics" />

            <div className="row stats_colHeaders">
                <div className="stats_colHeader stats_colName">Name</div>
                <div className="stats_colHeader stats_colEmail">Information</div>
            </div>

            <div className="stats_list">
                <ErrorBoundary>
                    {loadStatus === AsyncStatus.Loading && <Loader />}
                    {loadStatus === AsyncStatus.Success && stats && (<>

                        <div className="stats_listItem">
                            <div className="stats_colName">MQTT Status</div>
                            {stats.mqttIsConnected && <>
                                <div className="stats_colEmail">
                                    <p className="stats_success">Connected</p>
                                </div></>}
                            {!stats.mqttIsConnected && <>
                                <div className="stats_colEmail">
                                    <p className="stats_error">Disconnected</p>
                                </div></>}
                        </div>
                        <div className="stats_listItem">
                            <div className="stats_colName">MQTT Last Success</div>
                            <div className="stats_colEmail"><p>{stats.mqttLastSuccess}</p></div>
                        </div>
                        <div className="stats_listItem">
                            <div className="stats_colName">Server Start Time</div>
                            <div className="stats_colEmail"><p>{stats.serverStartTime}</p></div>
                        </div>
                        <div className="stats_listItem">
                            <div className="stats_colName">Device Cache Success</div>
                            <div className="stats_colEmail"><p>{stats.deviceCacheLastSuccess}</p></div>
                        </div>
                        <div className="stats_listItem">
                            <div className="stats_colName">Location Cache Success</div>
                            <div className="stats_colEmail"><p>{stats.locationCacheLastSuccess}</p></div>
                        </div>
                        <div className="stats_listItem">
                            <div className="stats_colName">Snow Sync Success</div>
                            <div className="stats_colEmail"><p>{stats.snowSyncLastSuccess}</p></div>
                        </div>

                    </>)}
                    {loadStatus === AsyncStatus.Success && !stats && (
                        <div className="stats_listEmpty">
                            <FontAwesomeIcon
                                icon="search"
                                className="stats_listEmptyIcon"
                            />
                            <div className="stats_listEmptyText">
                                No stats found.
                            </div>
                        </div>
                    )}
                    {loadStatus === AsyncStatus.Error && (
                        <ErrorMessage message={loadError} />
                    )}
                </ErrorBoundary>
            </div>

            <div>
                <h2 className="emergencyStop_header">Emergency Stop</h2>
                <p>
                    In case of an emergency, you can immediately stop the server from pushing updates.
                </p>
                <ul>
                    <li>This should only be used in case of a security breach or major malfunction.</li>
                    <li>If a bad update is being deployed, try to cancel the update first.</li>
                    <li>The server will need to be manually reset before it can send updates again.</li>
                </ul>
                <Button onClick={() => setShowEmergencyStop(true)}>
                    Stop Server
                </Button>
            </div>

        </Page>
        <EmergencyStopModal show={showEmergencyStop} onHide={() => setShowEmergencyStop(false)} />
    </>);
}